import React, { useState, useEffect, useMemo, useCallback, useTransition } from 'react';
import axios from 'axios';
import { APIURL } from '../../../../config';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Checkbox,
    FormControlLabel,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    InputAdornment,
    Chip,
    Divider,
    CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import ContactsIcon from '@mui/icons-material/Contacts';

const TabPanel = ({ children, value, tab }) => {
    return value === tab ? <Box sx={{ mt: 2 }}>{children}</Box> : null;
};

const CustomerItem = React.memo(({ customer, isSelected, onToggle, customerLabel = 'Customer' }) => (
    <ListItem button onClick={() => onToggle(customer.cus_id)} dense>
        <ListItemIcon>
            <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple />
        </ListItemIcon>
        <ListItemText 
            primary={customer.cus_name || `Unnamed ${customerLabel.replace(/s$/, '')}`}
            secondary={
                <>
                    {customer.cus_email && <span style={{ fontSize: '0.75rem', color: '#666' }}>{customer.cus_email}</span>}
                    {customer.cus_email && customer.ddd_id && <span style={{ margin: '0 4px' }}>•</span>}
                    {customer.ddd_id && <span style={{ fontSize: '0.75rem', color: '#666' }}>DDDID: {customer.ddd_id}</span>}
                </>
            }
        />
    </ListItem>
));

const ContactItem = React.memo(({ contact, isSelected, onToggle }) => (
    <ListItem button onClick={() => onToggle(contact.con_id)} dense>
        <ListItemIcon>
            <Checkbox edge="start" checked={isSelected} tabIndex={-1} disableRipple />
        </ListItemIcon>
        <ListItemText
            primary={`${contact.con_first_name || ''} ${contact.con_last_name || ''}`.trim() || 'Unnamed Contact'}
            secondary={
                <>
                    {contact.con_email && <span style={{ fontSize: '0.75rem', color: '#666' }}>{contact.con_email}</span>}
                    {contact.con_email && contact.con_phone && <span style={{ margin: '0 4px' }}>•</span>}
                    {contact.con_phone && <span style={{ fontSize: '0.75rem', color: '#666' }}>{contact.con_phone}</span>}
                </>
            }
        />
    </ListItem>
));

const MemberSelection = ({ selectedMembers, setSelectedMembers, comp_id, emp_id, emp_type, customerLabel = 'Customers' }) => {
    const [currentTab, setCurrentTab] = useState(0);
    const [isPending, startTransition] = useTransition();
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [contactSearchTerm, setContactSearchTerm] = useState('');
    const [customers, setCustomers] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const [isLoadingContacts, setIsLoadingContacts] = useState(false);

    // Load customers
    useEffect(() => {
        const fetchCustomers = async () => {
            setIsLoadingCustomers(true);
            try {
                const response = await axios.get(`${APIURL}/customers/v2/associated`, {
                    params: { 
                        company_id: comp_id,
                        employee_id: emp_id,
                        type: emp_type
                    }
                });
                setCustomers(response.data || []);
            } catch (error) {
                console.error('Error fetching customers:', error);
                setCustomers([]);
            } finally {
                setIsLoadingCustomers(false);
            }
        };

        if (comp_id && emp_id) {
            fetchCustomers();
        }
    }, [comp_id, emp_id, emp_type]);

    // Load contacts
    useEffect(() => {
        const fetchContacts = async () => {
            setIsLoadingContacts(true);
            try {
                const response = await axios.get(`${APIURL}/contacts`, {
                    params: { com_id: comp_id }
                });
                setContacts(response.data || []);
            } catch (error) {
                console.error('Error fetching contacts:', error);
                setContacts([]);
            } finally {
                setIsLoadingContacts(false);
            }
        };

        if (comp_id) {
            fetchContacts();
        }
    }, [comp_id]);

    // Filter customers based on search (memoized)
    const filteredCustomers = useMemo(() => {
        if (!customerSearchTerm) return customers;
        const searchLower = customerSearchTerm.toLowerCase();
        return customers.filter(customer => {
            const name = customer.cus_name?.toLowerCase() || '';
            const email = customer.cus_email?.toLowerCase() || '';
            const dddId = customer.ddd_id?.toLowerCase() || '';
            return name.includes(searchLower) || email.includes(searchLower) || dddId.includes(searchLower);
        });
    }, [customers, customerSearchTerm]);

    // Filter contacts based on search (memoized)
    const filteredContacts = useMemo(() => {
        if (!contactSearchTerm) return contacts;
        const searchLower = contactSearchTerm.toLowerCase();
        return contacts.filter(contact => {
            const firstName = contact.con_first_name?.toLowerCase() || '';
            const lastName = contact.con_last_name?.toLowerCase() || '';
            const email = contact.con_email?.toLowerCase() || '';
            const phone = contact.con_phone?.toLowerCase() || '';
            return firstName.includes(searchLower) || 
                   lastName.includes(searchLower) || 
                   email.includes(searchLower) || 
                   phone.includes(searchLower);
        });
    }, [contacts, contactSearchTerm]);

    // Handle customer selection (memoized)
    const handleCustomerToggle = useCallback((customerId) => {
        setSelectedMembers(prev => ({
            ...prev,
            customers: prev.customers.includes(customerId)
                ? prev.customers.filter(id => id !== customerId)
                : [...prev.customers, customerId]
        }));
    }, [setSelectedMembers]);

    // Handle contact selection (memoized)
    const handleContactToggle = useCallback((contactId) => {
        setSelectedMembers(prev => ({
            ...prev,
            contacts: prev.contacts.includes(contactId)
                ? prev.contacts.filter(id => id !== contactId)
                : [...prev.contacts, contactId]
        }));
    }, [setSelectedMembers]);

    // Select all filtered customers
    const handleSelectAllCustomers = () => {
        const allFilteredIds = filteredCustomers.map(c => c.cus_id);
        const allSelected = allFilteredIds.every(id => selectedMembers.customers.includes(id));
        
        if (allSelected) {
            // Deselect all filtered
            setSelectedMembers(prev => ({
                ...prev,
                customers: prev.customers.filter(id => !allFilteredIds.includes(id))
            }));
        } else {
            // Select all filtered
            const newSelection = [...new Set([...selectedMembers.customers, ...allFilteredIds])];
            setSelectedMembers(prev => ({
                ...prev,
                customers: newSelection
            }));
        }
    };

    // Select all filtered contacts
    const handleSelectAllContacts = () => {
        const allFilteredIds = filteredContacts.map(c => c.con_id);
        const allSelected = allFilteredIds.every(id => selectedMembers.contacts.includes(id));
        
        if (allSelected) {
            // Deselect all filtered
            setSelectedMembers(prev => ({
                ...prev,
                contacts: prev.contacts.filter(id => !allFilteredIds.includes(id))
            }));
        } else {
            // Select all filtered
            const newSelection = [...new Set([...selectedMembers.contacts, ...allFilteredIds])];
            setSelectedMembers(prev => ({
                ...prev,
                contacts: newSelection
            }));
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Select {customerLabel.toLowerCase()} and/or contacts who are members of this household
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Chip 
                    label={`${selectedMembers.customers.length} ${customerLabel}${selectedMembers.customers.length !== 1 && !customerLabel.endsWith('s') ? 's' : ''} Selected`} 
                    color="primary" 
                    sx={{ mr: 1 }}
                />
                <Chip 
                    label={`${selectedMembers.contacts.length} Contact${selectedMembers.contacts.length !== 1 ? 's' : ''} Selected`} 
                    color="secondary"
                />
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Tabs value={currentTab} onChange={(e, v) => startTransition(() => setCurrentTab(v))} sx={{ mb: 2, minHeight: '40px' }}>
                <Tab icon={<PersonIcon />} label={customerLabel} iconPosition="start" sx={{ minHeight: '40px', py: 1 }} />
                <Tab icon={<ContactsIcon />} label="Contacts" iconPosition="start" sx={{ minHeight: '40px', py: 1 }} />
            </Tabs>

            {isPending ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
            <TabPanel value={currentTab} tab={0}>
                <Box>
                    <TextField
                        fullWidth
                        placeholder={`Search ${customerLabel.toLowerCase()} by name, email, or ID...`}
                        value={customerSearchTerm}
                        onChange={(e) => setCustomerSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />

                    {filteredCustomers.length > 0 && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filteredCustomers.every(c => selectedMembers.customers.includes(c.cus_id))}
                                    indeterminate={
                                        filteredCustomers.some(c => selectedMembers.customers.includes(c.cus_id)) &&
                                        !filteredCustomers.every(c => selectedMembers.customers.includes(c.cus_id))
                                    }
                                    onChange={handleSelectAllCustomers}
                                />
                            }
                            label="Select All"
                            sx={{ mb: 1 }}
                        />
                    )}

                    <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {isLoadingCustomers ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="textSecondary">
                                    Loading {customerLabel.toLowerCase()}...
                                </Typography>
                            </Box>
                        ) : filteredCustomers.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="textSecondary">
                                    {customerSearchTerm ? `No ${customerLabel.toLowerCase()} found matching your search` : `No ${customerLabel.toLowerCase()} available`}
                                </Typography>
                            </Box>
                        ) : (
                            <List>
                                {filteredCustomers.map((customer) => (
                                    <CustomerItem
                                        key={customer.cus_id}
                                        customer={customer}
                                        isSelected={selectedMembers.customers.includes(customer.cus_id)}
                                        onToggle={handleCustomerToggle}
                                        customerLabel={customerLabel}
                                    />
                                ))}
                            </List>
                        )}
                    </Paper>
                </Box>
            </TabPanel>

            <TabPanel value={currentTab} tab={1}>
                <Box>
                    <TextField
                        fullWidth
                        placeholder="Search contacts by name, email, or phone..."
                        value={contactSearchTerm}
                        onChange={(e) => setContactSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />

                    {filteredContacts.length > 0 && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filteredContacts.every(c => selectedMembers.contacts.includes(c.con_id))}
                                    indeterminate={
                                        filteredContacts.some(c => selectedMembers.contacts.includes(c.con_id)) &&
                                        !filteredContacts.every(c => selectedMembers.contacts.includes(c.con_id))
                                    }
                                    onChange={handleSelectAllContacts}
                                />
                            }
                            label="Select All"
                            sx={{ mb: 1 }}
                        />
                    )}

                    <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {isLoadingContacts ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="textSecondary">
                                    Loading contacts...
                                </Typography>
                            </Box>
                        ) : filteredContacts.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="textSecondary">
                                    {contactSearchTerm ? 'No contacts found matching your search' : 'No contacts available'}
                                </Typography>
                            </Box>
                        ) : (
                            <List>
                                {filteredContacts.map((contact) => (
                                    <ContactItem
                                        key={contact.con_id}
                                        contact={contact}
                                        isSelected={selectedMembers.contacts.includes(contact.con_id)}
                                        onToggle={handleContactToggle}
                                    />
                                ))}
                            </List>
                        )}
                    </Paper>
                </Box>
            </TabPanel>
                </>
            )}
        </Box>
    );
};

export default MemberSelection;
